import { useLingui } from '@lingui/react/macro'
import { useGeckoAnalytics } from 'features/analytics'
import { DataLayer } from 'features/systemLens'
import { useScatterplotContext } from 'features/timeSeries'
import { hoverUnit } from 'features/timeSeries/components/timeSeriesScatterplot/chartLegendLabel'
import { UPlotSeriesConfig } from 'features/timeSeries/utils/uPlotData'
import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import {
  EnhancedSensorEntry,
  LocalThresholdOverrideType,
  SensorThresholdConfig,
} from 'types'
import uPlot from 'uplot'

// Analytics property values are stable identifiers, not localized display copy.
/* eslint-disable lingui/no-unlocalized-strings */
const CONFIGURE_THRESHOLDS_EVENT = {
  button_name: 'Configure Thresholds',
  page: 'Asset Lens',
  tab: 'Analysis',
} as const
/* eslint-enable lingui/no-unlocalized-strings */

interface UseChartInteractionFunctionsProps {
  clearCursorData: () => void
  allSensorsToRender: Array<{ id: string; name: string }>
  enhancedSensorEntries: EnhancedSensorEntry[]
  updateCursorData: (
    sensorId: string,
    value: number,
    timestamp: number,
    unit: string
  ) => void
  setShowThresholdModal: Dispatch<SetStateAction<boolean>>
  selectedSensors: Array<{ id: string }>
  unitID?: string
  unitName?: string
  siteID: string
  setLocalThresholdOverrides: Dispatch<
    SetStateAction<LocalThresholdOverrideType>
  >
  setDataLayers: Dispatch<SetStateAction<DataLayer[]>>
}

export const useChartInteractionFunctions = ({
  clearCursorData,
  allSensorsToRender,
  enhancedSensorEntries,
  updateCursorData,
  setShowThresholdModal,
  selectedSensors,
  unitID,
  unitName,
  siteID,
  setLocalThresholdOverrides,
  setDataLayers,
}: UseChartInteractionFunctionsProps) => {
  const { t } = useLingui()

  const { derivedValues, unitMapping } = useScatterplotContext()
  const { trackButtonClicked } = useGeckoAnalytics()
  const [cursorPosition, setCursorPosition] = useState<{
    x: number
    y: number
    clientX: number
    clientY: number
  } | null>(null)

  const handleUnhover = useCallback(() => {
    clearCursorData()
    setCursorPosition(null)
  }, [clearCursorData, setCursorPosition])

  const handleCursorMove = useCallback(
    (idx: number | null, u: uPlot) => {
      if (idx === null || idx === undefined) {
        handleUnhover()
        return
      }

      ;(u.series as UPlotSeriesConfig[]).forEach((series, seriesIdx) => {
        if (seriesIdx === 0 || !series.show) return

        if (series._isRegression) return

        const sensorId = series._sensorId
        if (!sensorId) return

        const yVal = u.data[seriesIdx]?.[idx]
        const xVal = u.data[0]?.[idx]

        if (
          yVal !== null &&
          yVal !== undefined &&
          xVal !== null &&
          xVal !== undefined
        ) {
          const sensor = allSensorsToRender.find((s) => s.id === sensorId)
          if (!sensor) return

          const sensorEntry = enhancedSensorEntries.find(
            (e) => e.id === sensorId
          )
          const displayName = sensorEntry?.data?.name || sensorId
          const unit =
            hoverUnit(
              { id: sensorId, name: displayName },
              { derivedValues, unitMapping }
            ) ?? t`No Unit`
          updateCursorData(sensorId, yVal as number, xVal as number, unit)
        }
      })
    },
    [
      handleUnhover,
      allSensorsToRender,
      enhancedSensorEntries,
      derivedValues,
      unitMapping,
      updateCursorData,
      t,
    ]
  )

  const handleConfigureThresholds = useCallback(() => {
    setShowThresholdModal(true)
    trackButtonClicked({
      button_name: CONFIGURE_THRESHOLDS_EVENT.button_name,
      sensors_selected_count: selectedSensors.length,
      unit_id: unitID || '',
      unit_name: unitName,
      site_id: siteID,
      page: CONFIGURE_THRESHOLDS_EVENT.page,
      tab: CONFIGURE_THRESHOLDS_EVENT.tab,
    })
  }, [
    setShowThresholdModal,
    trackButtonClicked,
    selectedSensors.length,
    unitID,
    unitName,
    siteID,
  ])

  const handleSaveThresholdConfigurations = useCallback(
    (thresholdConfigs: SensorThresholdConfig[]) => {
      const overrides: LocalThresholdOverrideType = {}
      thresholdConfigs.forEach((config) => {
        if (config.enabled) {
          overrides[config.sensorId] = {
            sensorId: config.sensorId,
            enabled: true,
            lowerLowerThreshold: config.lowerLowerThreshold || null,
            lowerThreshold: config.lowerThreshold || null,
            upperThreshold: config.upperThreshold || null,
            upperUpperThreshold: config.upperUpperThreshold || null,
          }
        } else {
          overrides[config.sensorId] = {
            enabled: false,
            sensorId: config.sensorId,
            lowerLowerThreshold: null,
            lowerThreshold: null,
            upperThreshold: null,
            upperUpperThreshold: null,
          }
        }
      })

      setLocalThresholdOverrides((prev) => ({ ...prev, ...overrides }))
    },
    [setLocalThresholdOverrides]
  )

  const toggleLayer = useCallback(
    (layerId: string) => {
      setDataLayers((prev) =>
        prev.map((layer) =>
          layer.id === layerId ? { ...layer, enabled: !layer.enabled } : layer
        )
      )
    },
    [setDataLayers]
  )

  return {
    handleUnhover,
    handleCursorMove,
    handleConfigureThresholds,
    handleSaveThresholdConfigurations,
    toggleLayer,
    cursorPosition,
    setCursorPosition,
  }
}
